import { CashMovementRecord } from "../models/cashMovement";
import { PaymentData } from "../models/payment";
import { ReturnItemData } from "../models/credit";

export type ShopCashSummary = {
	cashReceived: number;
	grossCashReceived: number;
	upiReceived: number;
	cashRefunded: number;
	partnerWithdrawals: number;
	partnerCashWithdrawals: number;
	partnerUpiWithdrawals: number;
	shopExpenses: number;
	shopCashExpenses: number;
	shopUpiExpenses: number;
	availableCash: number;
	availableUpi: number;
};

export const calculateCashRefunds = (
	returns: ReturnItemData[],
	allPayments: PaymentData[],
	invoiceTotalsByInvoiceNumber: Record<string, number> = {}
) => {
	const refundUsedByInvoice: Record<string, number> = {};
	const outstandingCompensatedByInvoice: Record<string, number> = {};

	const sortedReturns = [...returns].sort(
		(a, b) =>
			new Date(a.returnDate).getTime() - new Date(b.returnDate).getTime()
	);

	return sortedReturns.reduce((sum, returnedItem) => {
		const returnAmount = returnedItem.amount * returnedItem.quantity;
		const paymentsForInvoice = allPayments.filter(
			(payment) =>
				payment.invoiceNumber === returnedItem.invoiceNumber &&
				new Date(payment.paymentDate).getTime() <=
					new Date(returnedItem.returnDate).getTime()
		);
		const totalPaid = paymentsForInvoice.reduce(
			(total, payment) => total + payment.amount,
			0
		);
		const invoiceTotal =
			invoiceTotalsByInvoiceNumber[returnedItem.invoiceNumber] || 0;
		const originalOutstanding = invoiceTotal - totalPaid;
		const alreadyCompensatedOutstanding =
			outstandingCompensatedByInvoice[returnedItem.invoiceNumber] || 0;
		const currentOutstanding =
			originalOutstanding - alreadyCompensatedOutstanding;

		const outstandingReduction = Math.min(
			Math.max(0, currentOutstanding),
			returnAmount
		);
		const cashRefundNeeded = Math.max(
			0,
			returnAmount - outstandingReduction
		);

		outstandingCompensatedByInvoice[returnedItem.invoiceNumber] =
			alreadyCompensatedOutstanding + outstandingReduction;

		const alreadyRefunded =
			refundUsedByInvoice[returnedItem.invoiceNumber] || 0;
		const availableRefund = Math.max(0, totalPaid - alreadyRefunded);
		const cashRefund = Math.min(cashRefundNeeded, availableRefund);
		refundUsedByInvoice[returnedItem.invoiceNumber] =
			alreadyRefunded + cashRefund;
		return sum + cashRefund;
	}, 0);
};

export const calculateShopCashSummary = ({
	paymentsInRange,
	returnsInRange,
	cashMovements,
	invoiceTotalsByInvoiceNumber = {}
}: {
	paymentsInRange: PaymentData[];
	returnsInRange: ReturnItemData[];
	cashMovements: CashMovementRecord[];
	invoiceTotalsByInvoiceNumber?: Record<string, number>;
}): ShopCashSummary => {
	const activeMovements = cashMovements.filter(
		(movement) => !movement.isVoided
	);
	const grossCashReceived = paymentsInRange
		.filter((payment) => payment.mode === "CASH")
		.reduce((sum, payment) => sum + payment.amount, 0);
	const upiReceived = paymentsInRange
		.filter((payment) => payment.mode === "UPI")
		.reduce((sum, payment) => sum + payment.amount, 0);
	const cashRefunded = calculateCashRefunds(
		returnsInRange,
		paymentsInRange,
		invoiceTotalsByInvoiceNumber
	);
	const cashReceived = grossCashReceived - cashRefunded;
	const partnerCashWithdrawals = activeMovements
		.filter(
			(movement) =>
				movement.type === "PARTNER_WITHDRAWAL" &&
				movement.paymentSource === "CASH"
		)
		.reduce((sum, movement) => sum + movement.amount, 0);
	const partnerUpiWithdrawals = activeMovements
		.filter(
			(movement) =>
				movement.type === "PARTNER_WITHDRAWAL" &&
				movement.paymentSource === "UPI"
		)
		.reduce((sum, movement) => sum + movement.amount, 0);
	const partnerWithdrawals = partnerCashWithdrawals + partnerUpiWithdrawals;
	const shopCashExpenses = activeMovements
		.filter(
			(movement) =>
				movement.type === "EXPENSE" && movement.paymentSource === "CASH"
		)
		.reduce((sum, movement) => sum + movement.amount, 0);
	const shopUpiExpenses = activeMovements
		.filter(
			(movement) =>
				movement.type === "EXPENSE" && movement.paymentSource === "UPI"
		)
		.reduce((sum, movement) => sum + movement.amount, 0);
	const shopExpenses = shopCashExpenses + shopUpiExpenses;

	return {
		cashReceived, // grossCashReceived - cashRefunded
		grossCashReceived,
		upiReceived,
		cashRefunded,
		partnerWithdrawals,
		partnerCashWithdrawals,
		partnerUpiWithdrawals,
		shopExpenses,
		shopCashExpenses,
		shopUpiExpenses,
		availableCash: cashReceived - partnerCashWithdrawals - shopCashExpenses,
		availableUpi: upiReceived - partnerUpiWithdrawals - shopUpiExpenses
	};
};
